import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const CHUNK_RELOAD_KEY = 'stagecrew-chunk-reload-attempted';

function isChunkLoadError(error: Error) {
  return /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(
    `${error.name} ${error.message}`
  );
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    if (isChunkLoadError(error) && sessionStorage.getItem(CHUNK_RELOAD_KEY) !== 'true') {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
      window.location.reload();
      return;
    }
  }

  handleReset = () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center bg-gray-50">
          <div className="text-center px-4">
            <div className="text-6xl mb-4 text-red-500">!</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {this.state.error && isChunkLoadError(this.state.error)
                ? 'The app was updated while this page was open. Refresh to load the newest version.'
                : 'An unexpected error occurred. Please try again.'}
            </p>
            <button
              onClick={this.handleReset}
              className="bg-blue-600 text-white px-6 py-3 rounded font-semibold hover:bg-blue-700"
            >
              {this.state.error && isChunkLoadError(this.state.error) ? 'Refresh App' : 'Return Home'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
